/**
 * De lesvoorstelkaart van "Leer dit" (sessie 84), gedeeld door het
 * copilootvlak in de zaak (v6) en de copiloot op de andere pagina's.
 *
 * Wat JP ziet, van boven naar onder:
 * - kop "Les voor Nova en Sanne" met het soort (Gedragsregel of Feit);
 * - de uitgeschreven les, met chips voor vraagtypen en kanalen;
 * - waarschuwingen, en uitklapbaar de onderbouwing en bronnen (alleen intern);
 * - geweigerd door een vangrail: een rood blok met welke en waarom, geen Akkoord;
 * - botsingen: per botsing de bestaande regel of kaart met een keuze;
 * - de herspeling: oud en nieuw antwoord naast elkaar (smal onder elkaar);
 * - de kosten van het voorbeeld, klein;
 * - onderaan één knop Akkoord en een tekstlink Niet.
 *
 * Ronde 2: een 409 haalt de les opnieuw op (Akkoord volgt dan de nieuwe
 * stand), Niet en Terugdraaien staan uit met reden als het account ze
 * niet mag, en een zaak zonder antwoord zegt waarom.
 *
 * Harde regel van deze repo: een uitgeschakelde knop staat er mét reden.
 */
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useInRouterContext } from "react-router-dom";

import { Icoon } from "../../WerkplekV6/Icoon";

import {
  type BotsingKeuze,
  type HerspeelZaak,
  type Les,
  type LesBotsing,
  herspelingLoopt,
  LES_TELLER_VANAF,
  lesEuro,
  lesFout,
  lesSleutel,
  lesTeLang,
  leerDitApi,
  MAX_LES_TEKENS,
  useLes,
} from "./leerDit";

import "./leerdit.css";

const SOORT_LABEL: Record<Les["soort"], string> = {
  regel: "Gedragsregel",
  feit: "Feit",
};

const KANAAL_LABEL: Record<string, string> = {
  chat: "Chat",
  whatsapp: "WhatsApp",
  mail: "Mail",
  email: "Mail",
  telefoon: "Telefoon",
};

const KEUZE_LABEL: Record<string, string> = {
  vervang: "Nieuwe les vervangt deze",
  naast: "Allebei houden",
};

const BOTSING_MET: Record<LesBotsing["met"], string> = {
  regel: "een bestaande regel",
  kaart: "een waarheidskaart",
  gedragsdocument: "het gedragsdocument",
};

/** Een vangrail-sleutel leesbaar maken: "geen_klantdata" -> "Geen klantdata". */
function vangrailNaam(sleutel: string): string {
  const woorden = sleutel.replace(/[_-]+/g, " ").trim();
  return woorden.charAt(0).toUpperCase() + woorden.slice(1);
}

/** Alleen een echte webadres wordt een link; de rest platte tekst. */
function isWebadres(url: string): boolean {
  return /^https?:\/\//i.test(url.trim());
}

function kanaalNaam(kanaal: string): string {
  return KANAAL_LABEL[kanaal.toLowerCase()] ?? kanaal;
}

/** Link naar Kennis; zonder router (enkele tests) een gewone link. */
function KennisLink({ naar, children }: { naar: string; children: string }) {
  const inRouter = useInRouterContext();
  return inRouter ? (
    <Link to={naar} className="les-link">
      {children}
    </Link>
  ) : (
    <a href={naar} className="les-link">
      {children}
    </a>
  );
}

export function LesKaart({ les: begin, onSluit }: { les: Les; onSluit?: () => void }) {
  const qc = useQueryClient();
  const query = useLes(begin);
  const les = query.data ?? begin;
  const [keuzes, setKeuzes] = useState<Record<string, BotsingKeuze>>({});
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);

  async function doe(wat: string, actie: () => Promise<Les>) {
    if (bezig) return;
    setBezig(true);
    setFout(null);
    try {
      const nieuw = await actie();
      qc.setQueryData(lesSleutel(les.id), nieuw);
    } catch (e) {
      const melding = lesFout(e, wat);
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        // De les is intussen veranderd: de nieuwste stand ophalen, zodat
        // Akkoord en de rest de echte stand volgen.
        try {
          const vers = await leerDitApi.haal(les.id);
          qc.setQueryData(lesSleutel(les.id), vers);
          setKeuzes({});
          setFout(`${melding} De kaart toont nu de nieuwste stand.`);
        } catch {
          setFout(`${melding} Ververs het scherm voor de nieuwste stand.`);
        }
      } else {
        setFout(melding);
      }
    } finally {
      setBezig(false);
    }
  }

  const openBotsingen = les.botsingen.filter((b) => !keuzes[b.id]);
  const redenUit = !les.mag_vastzetten
    ? les.reden_niet_vastzetten || "Vastzetten kan nu niet; de server gaf geen reden."
    : openBotsingen.length > 0
      ? "Kies eerst bij elke botsing wat er moet gebeuren."
      : null;

  const gefaald = les.vangrails.filter((v) => !v.geslaagd);
  const waarschuwingen = [...les.waarschuwingen];
  if (
    les.soort === "feit" &&
    !les.online_nagezocht &&
    !waarschuwingen.some((w) => /online/i.test(w))
  ) {
    waarschuwingen.push("Niet online nagezocht. Controleer het feit zelf.");
  }

  return (
    <div
      className="les-kaart"
      data-testid="les-kaart"
      data-status={les.status}
      aria-label="Lesvoorstel"
      role="group"
    >
      <div className="les-kop">
        <Icoon naam="kennis" klein />
        <b>Les voor Nova en Sanne</b>
        <span className="les-soort" data-testid="les-soort">
          {SOORT_LABEL[les.soort] ?? les.soort}
        </span>
      </div>

      {les.titel ? <div className="les-titel">{les.titel}</div> : null}
      {les.tekst ? (
        <p className="les-tekst" data-testid="les-tekst">
          {les.tekst}
        </p>
      ) : null}

      {les.vraagtypen.length + les.kanalen.length > 0 ? (
        <div className="les-chips">
          {les.vraagtypen.map((v) => (
            <span key={`v-${v}`} className="les-chip">
              {v}
            </span>
          ))}
          {les.kanalen.map((k) => (
            <span key={`k-${k}`} className="les-chip les-chip-kanaal">
              {kanaalNaam(k)}
            </span>
          ))}
        </div>
      ) : null}

      {waarschuwingen.length > 0 ? (
        <ul className="les-waarschuwing" data-testid="les-waarschuwingen">
          {waarschuwingen.map((w, i) => (
            <li key={i}>{w}</li>
          ))}
        </ul>
      ) : null}

      {les.onderbouwing || les.bronnen.length > 0 ? (
        <details className="les-intern" data-testid="les-intern">
          <summary>
            Onderbouwing en bronnen <small>alleen intern</small>
          </summary>
          {les.onderbouwing ? <p>{les.onderbouwing}</p> : null}
          {les.bronnen.length > 0 ? (
            <ul>
              {les.bronnen.map((b, i) => (
                <li key={i}>
                  {isWebadres(b.url) ? (
                    <a href={b.url.trim()} target="_blank" rel="noreferrer noopener">
                      {b.titel || b.url}
                    </a>
                  ) : (
                    <span data-testid="les-bron-tekst">
                      {b.titel && b.url && b.titel !== b.url
                        ? `${b.titel} (${b.url})`
                        : b.titel || b.url}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          ) : null}
        </details>
      ) : null}

      {les.status === "geweigerd" ? (
        <div className="les-geweigerd" role="alert" data-testid="les-geweigerd">
          <b>Deze les gaat niet door.</b>
          {gefaald.map((v) => (
            <div key={v.sleutel} className="les-vangrail">
              <span>{vangrailNaam(v.sleutel)}</span>: {v.uitleg}
            </div>
          ))}
          {les.geweigerd_reden &&
          !gefaald.some((v) => v.uitleg.trim() === les.geweigerd_reden?.trim()) ? (
            <div>{les.geweigerd_reden}</div>
          ) : null}
          {gefaald.length === 0 && !les.geweigerd_reden ? (
            <div>De server gaf geen reden op. Meld dit.</div>
          ) : null}
        </div>
      ) : null}

      {les.status === "voorstel" && les.botsingen.length > 0 ? (
        <div className="les-botsingen">
          {les.botsingen.map((b) => (
            <fieldset key={b.id} className="les-botsing" data-testid="les-botsing">
              <legend>
                Botst met {BOTSING_MET[b.met] ?? b.met}: <b>{b.titel}</b>
              </legend>
              {b.tekst ? <blockquote>{b.tekst}</blockquote> : null}
              {b.uitleg ? <div className="les-klein">{b.uitleg}</div> : null}
              <div className="les-keuzes">
                {b.keuzes.map((k) => (
                  <label key={k}>
                    <input
                      type="radio"
                      name={`les-${les.id}-botsing-${b.id}`}
                      value={k}
                      checked={keuzes[b.id] === k}
                      disabled={bezig}
                      onChange={() =>
                        setKeuzes((oud) => ({ ...oud, [b.id]: k as BotsingKeuze }))
                      }
                    />
                    {KEUZE_LABEL[k] ?? k}
                  </label>
                ))}
              </div>
            </fieldset>
          ))}
        </div>
      ) : null}

      {les.status !== "geweigerd" && les.status !== "mislukt" && les.status !== "bezig" ? (
        <Herspeling les={les} />
      ) : null}

      <div className="les-kosten" data-testid="les-kosten">
        {les.status === "geweigerd" ? "Kosten controle" : "Kosten voorbeeld"}:{" "}
        {lesEuro(les.kosten_eur)}
      </div>

      {fout ? (
        <div className="les-fout" role="alert" data-testid="les-fout">
          {fout}
        </div>
      ) : null}

      <LesVoet
        les={les}
        bezig={bezig}
        redenUit={redenUit}
        onAkkoord={() =>
          void doe("Niet vastgezet", () => leerDitApi.vastzetten(les.id, keuzes))
        }
        onNiet={() => void doe("Niet afgewezen", () => leerDitApi.afwijzen(les.id))}
        onTerug={() =>
          void doe("Niet teruggedraaid", () => leerDitApi.terugdraaien(les.id))
        }
        onSluit={onSluit}
      />
    </div>
  );
}

function LesVoet({
  les,
  bezig,
  redenUit,
  onAkkoord,
  onNiet,
  onTerug,
  onSluit,
}: {
  les: Les;
  bezig: boolean;
  redenUit: string | null;
  onAkkoord: () => void;
  onNiet: () => void;
  onTerug: () => void;
  onSluit?: () => void;
}) {
  const redenWijzigen =
    les.reden_niet_wijzigen || "Dat kan nu niet; de server gaf geen reden.";
  if (les.status === "voorstel") {
    const nietUit = les.mag_afwijzen === false;
    return (
      <div className="les-voet">
        <div className="les-knoppen">
          <button
            type="button"
            className="v6-knop doe les-akkoord"
            onClick={onAkkoord}
            disabled={bezig || redenUit !== null}
            aria-describedby={redenUit ? `les-reden-${les.id}` : undefined}
          >
            {bezig ? "Bezig..." : "Akkoord"}
          </button>
          <button
            type="button"
            className="les-tekstlink"
            onClick={onNiet}
            disabled={bezig || nietUit}
            aria-describedby={
              !nietUit
                ? undefined
                : redenWijzigen === redenUit
                  ? `les-reden-${les.id}`
                  : `les-reden-wijzig-${les.id}`
            }
          >
            Niet
          </button>
        </div>
        {redenUit ? (
          <div className="les-reden" id={`les-reden-${les.id}`} data-testid="les-reden-uit">
            {redenUit}
          </div>
        ) : null}
        {nietUit && redenWijzigen !== redenUit ? (
          <div
            className="les-reden"
            id={`les-reden-wijzig-${les.id}`}
            data-testid="les-reden-wijzigen"
          >
            {redenWijzigen}
          </div>
        ) : null}
      </div>
    );
  }

  if (les.status === "vastgezet") {
    const terugUit = les.mag_terugdraaien === false;
    const naar = les.soort === "feit" ? "/kennis?tab=kaarten" : "/kennis?tab=regels";
    const plek = les.soort === "feit" ? "Kennis > Waarheidskaarten" : "Kennis > Regels";
    return (
      <div className="les-voet">
        <div className="les-live" role="status" data-testid="les-live">
          <Icoon naam="ok" klein />
          <span>
            Staat live. Werkt vanaf het volgende bericht in chat, WhatsApp en mail.
          </span>
        </div>
        <div className="les-knoppen">
          <KennisLink naar={naar}>{`Bekijk in ${plek}`}</KennisLink>
          <button
            type="button"
            className="v6-knop les-terug"
            onClick={onTerug}
            disabled={bezig || terugUit}
            aria-describedby={terugUit ? `les-reden-wijzig-${les.id}` : undefined}
          >
            {bezig ? "Bezig..." : "Terugdraaien"}
          </button>
        </div>
        {terugUit ? (
          <div
            className="les-reden"
            id={`les-reden-wijzig-${les.id}`}
            data-testid="les-reden-wijzigen"
          >
            {redenWijzigen}
          </div>
        ) : null}
      </div>
    );
  }

  const slot =
    les.status === "teruggedraaid"
      ? "Teruggedraaid. Nova en Sanne gebruiken deze les niet meer."
      : les.status === "afgewezen"
        ? "Niet vastgezet. Er is niets veranderd."
        : les.status === "vervangen"
          ? "Vervangen door een nieuwere les. Deze les is niet meer actief."
          : les.status === "mislukt"
            ? "De les kon niet worden uitgeschreven. Er is niets veranderd. Probeer het opnieuw."
            : les.status === "bezig"
              ? "De copiloot schrijft de les nog uit."
              : "Er is niets veranderd.";
  return (
    <div className="les-voet">
      <div className="les-klein" role="status" data-testid="les-slot">
        {slot}
      </div>
      {onSluit ? (
        <div className="les-knoppen">
          <button type="button" className="les-tekstlink" onClick={onSluit}>
            Sluiten
          </button>
        </div>
      ) : null}
    </div>
  );
}

function Herspeling({ les }: { les: Les }) {
  const { herspeling } = les;
  if (herspeling.status === "bezig") {
    // Afgewezen of teruggedraaid: niemand wacht meer op het voorbeeld.
    if (!herspelingLoopt(les)) return null;
    return (
      <div className="les-herspeling les-klein" role="status" data-testid="les-herspeling-bezig">
        Nova en Sanne beantwoorden een paar echte vragen opnieuw met deze les. Dit
        kan een paar minuten duren.
      </div>
    );
  }
  if (herspeling.status === "overgeslagen") {
    return herspeling.reden ? (
      <div className="les-herspeling les-klein" data-testid="les-herspeling-overgeslagen">
        Geen voorbeeld gemaakt: {herspeling.reden.replace(/\.?$/, ".")}
      </div>
    ) : null;
  }
  if (herspeling.status === "mislukt") {
    return (
      <div className="les-herspeling les-mislukt" role="status" data-testid="les-herspeling-mislukt">
        Het voorbeeld lukte niet
        {herspeling.reden ? `: ${herspeling.reden.replace(/\.?$/, ".")}` : "."} Je
        kunt de les wel vastzetten.
      </div>
    );
  }
  if (herspeling.zaken.length === 0) {
    return (
      <div className="les-herspeling les-klein" data-testid="les-herspeling-leeg">
        Geen vergelijkbare vragen gevonden om opnieuw te spelen.
      </div>
    );
  }
  const zaken = [...herspeling.zaken].sort(
    (a, b) => Number(b.is_huidige) - Number(a.is_huidige),
  );
  return (
    <div className="les-herspeling" data-testid="les-herspeling">
      <div className="les-herspeling-kop">Wat verandert er</div>
      {zaken.map((z) => (
        <HerspeelBlok key={z.zaak_id} zaak={z} />
      ))}
    </div>
  );
}

function HerspeelBlok({ zaak }: { zaak: HerspeelZaak }) {
  return (
    <div className="les-zaak" data-testid="les-zaak" data-huidige={zaak.is_huidige || undefined}>
      <div className="les-zaak-kop">
        <b>{zaak.label}</b>
        {zaak.is_huidige ? <span className="les-chip">Deze zaak</span> : null}
        {zaak.kanaal ? <span className="les-chip les-chip-kanaal">{kanaalNaam(zaak.kanaal)}</span> : null}
      </div>
      {zaak.vraag_kort ? <div className="les-klein">{zaak.vraag_kort}</div> : null}
      <div className="les-vergelijk">
        <div className="les-oud" data-testid="les-oud">
          <div className="les-kol-kop">
            <b>Oud</b> Zo antwoorden ze nu
          </div>
          <Antwoord tekst={zaak.oud} fout={zaak.fout} />
        </div>
        <div className="les-nieuw" data-testid="les-nieuw">
          <div className="les-kol-kop">
            <b>Nieuw</b> Zo antwoorden ze met deze les
          </div>
          <Antwoord tekst={zaak.nieuw} fout={zaak.fout} />
        </div>
      </div>
    </div>
  );
}

/** Nooit een stil lege kolom: de reden, of dat er geen antwoord is. */
function Antwoord({ tekst, fout }: { tekst: string | null; fout: string | null }) {
  if (tekst && tekst.trim()) return <p>{tekst}</p>;
  return (
    <p className="les-geen-antwoord" data-testid="les-geen-antwoord">
      {fout && fout.trim() ? fout : "Geen antwoord beschikbaar"}
    </p>
  );
}

/**
 * Teller onder het invoerveld bij een les: in beeld vanaf 1800 tekens,
 * boven 2000 met de uitleg waarom versturen uit staat.
 */
export function LesTeller({ tekst, id }: { tekst: string; id?: string }) {
  const lengte = tekst.trim().length;
  if (lengte < LES_TELLER_VANAF) return null;
  const teLang = lesTeLang(tekst);
  return (
    <div
      id={id}
      className={`les-teller${teLang ? " te-lang" : ""}`}
      data-testid="les-teller"
      role={teLang ? "alert" : "status"}
    >
      {lengte} / {MAX_LES_TEKENS} tekens
      {teLang
        ? `. Een les mag maximaal ${MAX_LES_TEKENS} tekens zijn; versturen kan pas als hij korter is.`
        : null}
    </div>
  );
}
